/**
 * Solo-mode category rotation queue.
 *
 * Each press-start in solo mode pops the next category from a shuffled
 * queue. When the queue empties we reshuffle and ensure the new cycle
 * doesn't open with whatever category just closed the previous one
 * (anti-repeat across cycles). Net effect: across N solo plays where
 * N == CATEGORIES.length, the user sees every category exactly once
 * before any repeat, with no consecutive repeats at cycle boundaries.
 *
 * Bonded mode does NOT use this — both twins continue to share the
 * server-derived global daily seed.
 */

const QUEUE_KEY = "vutwin.solo.queue";
const LAST_KEY = "vutwin.solo.lastCategory";

function readStorage(): Storage | null {
  try {
    if (typeof localStorage === "undefined") return null;
    return localStorage;
  } catch {
    return null;
  }
}

function shuffleInPlace<T>(arr: T[]): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j]!, arr[i]!];
  }
  return arr;
}

function readQueue(): string[] {
  const ls = readStorage();
  if (!ls) return [];
  const raw = ls.getItem(QUEUE_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as string[];
    return Array.isArray(parsed) && parsed.every((x) => typeof x === "string")
      ? parsed
      : [];
  } catch {
    return [];
  }
}

function writeQueue(q: string[]): void {
  const ls = readStorage();
  if (!ls) return;
  try {
    ls.setItem(QUEUE_KEY, JSON.stringify(q));
  } catch {
    /* quota exceeded — solo will fall back to a fresh shuffle next time */
  }
}

function readLast(): string | null {
  const ls = readStorage();
  return ls ? ls.getItem(LAST_KEY) : null;
}

function writeLast(id: string): void {
  const ls = readStorage();
  if (!ls) return;
  try {
    ls.setItem(LAST_KEY, id);
  } catch {
    /* ignore */
  }
}

/**
 * Pop the next category id for the current solo session.
 *
 * `allIds` is filtered against the persisted queue — any stale ids no
 * longer in `allIds` are dropped. When refilling, the first slot is
 * swapped if it would repeat the previous cycle's tail.
 */
export function popNextCategory(allIds: string[]): string {
  if (allIds.length === 0) {
    throw new Error("popNextCategory called with empty category set");
  }

  const last = readLast();
  let queue = readQueue().filter((id) => allIds.includes(id));

  if (queue.length === 0) {
    queue = shuffleInPlace([...allIds]);
    if (queue.length > 1 && last && queue[0] === last) {
      [queue[0], queue[1]] = [queue[1]!, queue[0]!];
    }
  }

  const next = queue.shift()!;
  writeQueue(queue);
  writeLast(next);
  return next;
}

/** Test / debug only — wipe queue + last so the next pop reshuffles fresh. */
export function resetCategoryQueue(): void {
  const ls = readStorage();
  if (!ls) return;
  ls.removeItem(QUEUE_KEY);
  ls.removeItem(LAST_KEY);
}
